import { useMemo, useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  TouchApp,
  Business,
  VpnKey,
  Refresh,
  CheckCircle,
  Cancel,
  Add,
  ContentCopy,
  Search,
  FilterAltOff,
} from '@mui/icons-material';
import { useSuperAdminTouchPoints, useDeleteTouchPoint, useRegenerateTouchPointKey } from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { colors, transitions, radius } from '../../theme/theme';

export const TouchPointsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTouchPoint, setSelectedTouchPoint] = useState<any>(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regeneratedCredentials, setRegeneratedCredentials] = useState<{ username: string; key: string } | null>(null);

  const { touchPoints, isLoading, error } = useSuperAdminTouchPoints(page + 1, pageSize);
  const deleteTouchPoint = useDeleteTouchPoint();
  const regenerateKey = useRegenerateTouchPointKey();

  // Filters (client-side over the current page payload, mirroring the existing summary cards)
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [countyFilter, setCountyFilter] = useState<{ id: number; name: string } | null>(null);

  const rows: any[] = touchPoints?.data ?? [];

  const uniqueCounties = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r: any) => {
      if (r.county?.id && !seen.has(r.county.id)) seen.set(r.county.id, r.county.name);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r: any) => {
      if (countyFilter && r.county?.id !== countyFilter.id) return false;
      if (channelFilter && r.touch_point !== channelFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (q) {
        const hay = `${r.touch_point || ''} ${r.username || ''} ${r.county?.name || ''} ${r.display_phone_number || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, countyFilter, channelFilter, statusFilter]);

  const activeFilterCount = (search ? 1 : 0) + (countyFilter ? 1 : 0) + (channelFilter ? 1 : 0) + (statusFilter ? 1 : 0);
  const clearFilters = () => {
    setSearch('');
    setCountyFilter(null);
    setChannelFilter(null);
    setStatusFilter(null);
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar(`${label} copied to clipboard!`, 'success');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, touchPoint: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTouchPoint(touchPoint);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTouchPoint(null);
  };

  const handleEdit = () => {
    if (selectedTouchPoint) {
      navigate(`/superadmin/touch-points/${selectedTouchPoint.id}/edit`);
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    if (!selectedTouchPoint) return;

    if (window.confirm(`Are you sure you want to delete touch point "${selectedTouchPoint.name}"?`)) {
      try {
        await deleteTouchPoint.mutateAsync(selectedTouchPoint.id);
        showSnackbar('Touch point deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-points'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete touch point', 'error');
      }
    }
  };

  const handleOpenRegenerateDialog = () => {
    setRegenerateDialogOpen(true);
    // Close menu but DON'T clear selectedTouchPoint - we need it for regeneration
    setMenuAnchor(null);
  };

  const handleCloseRegenerateDialog = () => {
    setRegenerateDialogOpen(false);
    setRegeneratedCredentials(null);
    // Now we can safely clear the selected touch point
    setSelectedTouchPoint(null);
  };

  const handleConfirmRegenerate = async () => {

    if (!selectedTouchPoint) {
      console.error('No touch point selected!');
      return;
    }

    try {

      const response = await regenerateKey.mutateAsync(selectedTouchPoint.id);

      // Since apiClient.post returns response.data directly, and the backend returns
      // { status, data, message, credentials }, we should access credentials at response.credentials
      const creds = (response as any).credentials;

      if (creds && creds.username && creds.key) {
        setRegeneratedCredentials(creds);
        showSnackbar('API key regenerated successfully! Please save the new credentials.', 'success');
      } else {
        console.warn('No credentials found in response. Response structure:', response);
        showSnackbar('API key regenerated successfully', 'success');
        setRegenerateDialogOpen(false);
      }

      queryClient.invalidateQueries({ queryKey: ['superadmin', 'touch-points'] });
    } catch (error: any) {
      console.error('Regenerate error:', error);
      console.error('Error response:', error.response?.data);
      showSnackbar('Failed to regenerate API key', 'error');
      setRegenerateDialogOpen(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'touch_point',
      headerName: 'Touch Point',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TouchApp sx={{ fontSize: 18, color: colors.primary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{params.value || 'N/A'}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
              {params.row.username || 'No username'}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'county',
      headerName: 'County',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => row.county?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 16, color: colors.grey[500] }} />
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const isActive = params.value === 'active';
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
            sx={{ minWidth: 85 }}
          />
        );
      },
    },
    {
      field: 'transactions_count',
      headerName: 'Transactions',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => value || 0,
      renderCell: (params) => (
        <Chip
          label={params.value?.toLocaleString() || 0}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 110,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Touch Points"
          subtitle="Manage service delivery points across all counties"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Counties' },
            { label: 'Touch Points' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/superadmin/touch-points/create')}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Touch Point
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load touch points. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && touchPoints && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.primary,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.primary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Touch Points</Box>
                    <TouchApp sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {touchPoints.meta?.total?.toLocaleString() || touchPoints.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Across all counties</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.success,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.success.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Active</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {touchPoints.data?.filter((tp: any) => tp.status === 'active' || tp.status !== 'inactive').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently active</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.danger,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.error.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Inactive</Box>
                    <Cancel sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {touchPoints.data?.filter((tp: any) => tp.status === 'inactive').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently inactive</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.info,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.info.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Counties</Box>
                    <Business sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {new Set(touchPoints.data?.map((tp: any) => tp.county_id)).size || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>With touch points</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: colors.shadows.base,
            mb: 2,
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth size="small" placeholder="Search username, channel, county..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: colors.grey[500] }} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  size="small"
                  options={uniqueCounties}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={countyFilter}
                  onChange={(_, v) => setCountyFilter(v)}
                  renderInput={(params) => <TextField {...params} label="County" />}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <Autocomplete
                  size="small"
                  options={['portal', 'mobile', 'whatsapp']}
                  getOptionLabel={(o) => ({ portal: 'Citizen Portal', mobile: 'Mobile App', whatsapp: 'WhatsApp' } as Record<string, string>)[o] ?? o}
                  value={channelFilter}
                  onChange={(_, v) => setChannelFilter(v)}
                  renderInput={(params) => <TextField {...params} label="Channel" />}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <Autocomplete
                  size="small"
                  options={['active', 'inactive']}
                  getOptionLabel={(o) => o.charAt(0).toUpperCase() + o.slice(1)}
                  value={statusFilter}
                  onChange={(_, v) => setStatusFilter(v)}
                  renderInput={(params) => <TextField {...params} label="Status" />}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 1 }}>
                <Tooltip title={activeFilterCount ? 'Clear filters' : 'No filters active'}>
                  <span>
                    <IconButton
                      onClick={clearFilters}
                      disabled={!activeFilterCount}
                      sx={{
                        bgcolor: activeFilterCount ? `${colors.primary.main}10` : 'transparent',
                      }}
                    >
                      <FilterAltOff />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
            {activeFilterCount > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredRows.length} of {rows.length}
                </Typography>
                {countyFilter && <Chip size="small" label={`County: ${countyFilter.name}`} onDelete={() => setCountyFilter(null)} />}
                {channelFilter && <Chip size="small" label={`Channel: ${channelFilter}`} onDelete={() => setChannelFilter(null)} />}
                {statusFilter && <Chip size="small" label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter(null)} />}
                {search && <Chip size="small" label={`Search: "${search}"`} onDelete={() => setSearch('')} />}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'visible',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: colors.shadows.base,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={activeFilterCount > 0 ? filteredRows.length : (touchPoints?.meta?.total || touchPoints?.data?.length || 0)}
                  // When filters are active, the API page no longer matches what's shown — switch to client pagination over the filtered subset.
                  paginationMode={activeFilterCount > 0 ? 'client' : (touchPoints?.meta?.total ? 'server' : 'client')}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    borderRadius: 2,
                    '& .MuiDataGrid-main': {
                      borderRadius: 2,
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      borderTopLeftRadius: '16px',
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-columnHeader:first-of-type': {
                      borderTopLeftRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeader:last-of-type': {
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: transitions.fast,
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                      borderBottomLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" sx={{ color: colors.warning.main }} />
            </ListItemIcon>
            <ListItemText>Edit Touch Point</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenRegenerateDialog}>
            <ListItemIcon>
              <Refresh fontSize="small" sx={{ color: colors.info.main }} />
            </ListItemIcon>
            <ListItemText>Regenerate API Key</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete Touch Point</ListItemText>
          </MenuItem>
        </Menu>

        {/* Regenerate Key Dialog */}
        <Dialog open={regenerateDialogOpen} onClose={handleCloseRegenerateDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {regeneratedCredentials ? 'New API Key Generated' : 'Regenerate API Key'}
          </DialogTitle>
          <DialogContent>
            {regeneratedCredentials ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" paragraph>
                    New API key has been generated successfully. Please save these credentials as the API key will not be shown again.
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(0,0,0,0.05)',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        <strong>Username:</strong> {regeneratedCredentials.username}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopy(regeneratedCredentials.username, 'Username')}
                      >
                        Copy
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', mr: 2 }}>
                        <strong>API Key:</strong> {regeneratedCredentials.key}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopy(regeneratedCredentials.key, 'API Key')}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                </Alert>
              </Box>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Warning:</strong> This action cannot be undone.
                </Alert>
                <Typography variant="body2">
                  Are you sure you want to regenerate the API key for <strong>{selectedTouchPoint?.touch_point}</strong>?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  The current API key will immediately stop working and any systems using it will lose access.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {regeneratedCredentials ? (
              <Button onClick={handleCloseRegenerateDialog} variant="contained">
                Close
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseRegenerateDialog} disabled={regenerateKey.isPending}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmRegenerate}
                  color="warning"
                  variant="contained"
                  disabled={regenerateKey.isPending}
                >
                  {regenerateKey.isPending ? 'Regenerating...' : 'Regenerate Key'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
